import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, updateDoc, arrayUnion, increment } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "./Navbar";
import AudioPlayer from "./AudioPlayer";
import vocabData from "./vocabData";

function Practice() {
  const [practiceMode, setPracticeMode] = useState("vocabulary");
  const [difficulty, setDifficulty] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("Essential Greetings");
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const { currentUser } = useAuth();

  const practiceModes = [
    { id: "vocabulary", name: "Vocabulary Quiz", icon: "🧠" },
    { id: "sentence", name: "Sentence Practice", icon: "📝" },
    { id: "mixed", name: "Mixed Challenge", icon: "🎯" }
  ];

  const difficulties = [
    { id: "all", name: "All", color: "red" },
    { id: "beginner", name: "Beginner", color: "green" },
    { id: "intermediate", name: "Intermediate", color: "orange" },
    { id: "advanced", name: "Advanced", color: "red" }
  ];

  useEffect(() => {
    if (practiceMode === "vocabulary") {
      generateVocabularyQuiz();
    } else if (practiceMode === "sentence") {
      generateSentencePractice();
    } else if (practiceMode === "mixed") {
      generateMixedChallenge();
    }
  }, [practiceMode, selectedCategory, difficulty]);

  const generateVocabularyQuiz = () => {
    let words = vocabData[selectedCategory] || [];
    
    // Filter by difficulty if not "all"
    if (difficulty !== "all") {
      words = words.filter(word => word.difficulty?.toLowerCase() === difficulty);
    }
    
    if (words.length === 0) {
      words = vocabData[selectedCategory] || [];
    }

    const shuffled = [...words].sort(() => Math.random() - 0.5);
    const questions = shuffled.slice(0, 10).map((word, index) => {
      const wrongAnswers = words
        .filter(w => w.word !== word.word)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(w => w.meaning);
      
      const allAnswers = [word.meaning, ...wrongAnswers].sort(() => Math.random() - 0.5);
      
      return {
        ...word,
        answers: allAnswers,
        correctAnswer: word.meaning,
        question: `What does ${word.word} mean?`
      };
    });
    
    setQuestions(questions);
    setCurrentQuestion(questions[0]);
    setTotalQuestions(questions.length);
    setQuestionIndex(0);
    setScore(0);
    setQuizCompleted(false);
    setShowResult(false);
    setStreak(0);
  };

  const generateSentencePractice = () => {
    const sentenceQuestions = [
      {
        question: "Complete the sentence: '안녕하세요, 저는 ___ 입니다.'",
        answers: ["김철수", "김철수예요", "김철수입니다", "김철수야"],
        correctAnswer: "김철수입니다",
        explanation: "Use '입니다' for formal self-introduction."
      },
      {
        question: "How do you say 'I am a student' in Korean?",
        answers: ["저는 학생입니다", "저는 학생이에요", "나는 학생이야", "저는 학생이야"],
        correctAnswer: "저는 학생입니다",
        explanation: "Formal way to say 'I am a student'."
      },
      {
        question: "Complete: '오늘 날씨가 ___ 좋아요.'",
        answers: ["정말", "아주", "매우", "너무"],
        correctAnswer: "정말",
        explanation: "'정말' means 'really' and is commonly used."
      },
      {
        question: "How do you ask 'What is your name?' politely?",
        answers: ["이름이 뭐야?", "이름이 뭐예요?", "성함이 어떻게 되세요?", "이름이 뭐니?"],
        correctAnswer: "성함이 어떻게 되세요?",
        explanation: "Most polite way to ask for someone's name."
      },
      {
        question: "Complete: '한국어를 ___ 공부해요.'",
        answers: ["열심히", "자주", "많이", "잘"],
        correctAnswer: "열심히",
        explanation: "'열심히' means 'diligently' or 'hard'."
      },
      {
        question: "How do you say 'Thank you very much'?",
        answers: ["고마워요", "감사합니다", "정말 고마워요", "대단히 감사합니다"],
        correctAnswer: "대단히 감사합니다",
        explanation: "Most formal and polite way to express gratitude."
      },
      {
        question: "Complete: '저는 한국 음식을 ___ 좋아해요.'",
        answers: ["아주", "정말", "매우", "너무"],
        correctAnswer: "정말",
        explanation: "'정말' is commonly used to emphasize liking something."
      },
      {
        question: "How do you ask 'Where are you from?' politely?",
        answers: ["어디서 왔어?", "어디서 왔어요?", "어느 나라에서 오셨나요?", "어디 살아?"],
        correctAnswer: "어느 나라에서 오셨나요?",
        explanation: "Polite way to ask about someone's origin."
      },
      {
        question: "Complete: '한국어가 ___ 어려워요.'",
        answers: ["아주", "정말", "매우", "너무"],
        correctAnswer: "정말",
        explanation: "'정말' is used to emphasize difficulty."
      },
      {
        question: "How do you say 'Nice to meet you' formally?",
        answers: ["만나서 반가워", "만나서 반가워요", "만나서 반갑습니다", "반가워요"],
        correctAnswer: "만나서 반갑습니다",
        explanation: "Most formal way to say 'nice to meet you'."
      }
    ];

    setQuestions(sentenceQuestions);
    setCurrentQuestion(sentenceQuestions[0]);
    setTotalQuestions(sentenceQuestions.length);
    setQuestionIndex(0);
    setScore(0);
    setQuizCompleted(false);
    setShowResult(false);
    setStreak(0);
  };

  const generateMixedChallenge = () => {
    const mixedQuestions = [
      {
        question: "What does '안녕하세요' mean?",
        answers: ["Goodbye", "Hello", "Thank you", "Excuse me"],
        correctAnswer: "Hello",
        explanation: "Standard greeting in Korean."
      },
      {
        question: "Complete the sentence: '저는 ___ 살입니다.'",
        answers: ["스물", "이십", "20", "스무"],
        correctAnswer: "스물",
        explanation: "Use native Korean numbers for age."
      },
      {
        question: "What is the polite form of '먹다' (to eat)?",
        answers: ["먹어요", "드세요", "드시다", "드셔요"],
        correctAnswer: "드시다",
        explanation: "Honorific form of 'to eat'."
      },
      {
        question: "How do you say 'I don't understand' politely?",
        answers: ["모르겠어", "이해 안 돼", "이해가 안 돼요", "모르겠어요"],
        correctAnswer: "이해가 안 돼요",
        explanation: "Polite way to say you don't understand."
      },
      {
        question: "What does '고맙습니다' mean?",
        answers: ["Hello", "Goodbye", "Thank you", "Sorry"],
        correctAnswer: "Thank you",
        explanation: "Formal way to say thank you."
      },
      {
        question: "Complete: '오늘은 ___ 요일이에요?'",
        answers: ["무슨", "어떤", "몇", "언제"],
        correctAnswer: "무슨",
        explanation: "'무슨' means 'what kind of' or 'which'."
      },
      {
        question: "What is the Korean word for 'water'?",
        answers: ["물", "음료", "차", "주스"],
        correctAnswer: "물",
        explanation: "Basic word for water in Korean."
      },
      {
        question: "How do you ask 'How much is this?' politely?",
        answers: ["얼마야?", "얼마예요?", "이거 얼마에요?", "얼마죠?"],
        correctAnswer: "이거 얼마에요?",
        explanation: "Polite way to ask about price."
      },
      {
        question: "What does '죄송합니다' mean?",
        answers: ["Thank you", "Excuse me", "I'm sorry", "You're welcome"],
        correctAnswer: "I'm sorry",
        explanation: "Formal way to apologize."
      },
      {
        question: "Complete: '한국어를 ___ 배우고 있어요.'",
        answers: ["열심히", "자주", "많이", "잘"],
        correctAnswer: "열심히",
        explanation: "'열심히' means 'diligently' or 'hard'."
      }
    ];

    setQuestions(mixedQuestions);
    setCurrentQuestion(mixedQuestions[0]);
    setTotalQuestions(mixedQuestions.length);
    setQuestionIndex(0);
    setScore(0);
    setQuizCompleted(false);
    setShowResult(false);
    setStreak(0);
  };

  const resetQuiz = () => {
    if (practiceMode === "vocabulary") {
      generateVocabularyQuiz();
    } else if (practiceMode === "sentence") {
      generateSentencePractice();
    } else if (practiceMode === "mixed") {
      generateMixedChallenge();
    }
  };

  const handleAnswer = async (answer) => {
    if (showResult) return;
    
    setSelectedAnswer(answer);
    setCorrectAnswer(currentQuestion.correctAnswer);
    setShowResult(true);
    
    const isCorrect = answer === currentQuestion.correctAnswer;
    
    if (isCorrect) {
      setScore(prev => prev + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) {
          setBestStreak(newStreak);
        }
        return newStreak;
      });
    } else {
      setStreak(0);
    }

    // Save progress for authenticated users
    if (currentUser) {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, {
          quizScores: arrayUnion({
            category: selectedCategory,
            score: isCorrect ? 1 : 0,
            total: 1,
            timestamp: new Date()
          }),
          totalStudyTime: increment(1)
        });
      } catch (error) {
        console.error("Error saving quiz progress:", error);
      }
    }

    setTimeout(() => {
      if (questionIndex < totalQuestions - 1) {
        setQuestionIndex(prev => prev + 1);
        setCurrentQuestion(questions[questionIndex + 1]);
        setShowResult(false);
        setSelectedAnswer("");
        setCorrectAnswer("");
      } else {
        setQuizCompleted(true);
        setAccuracy(Math.round((score / totalQuestions) * 100));
      }
    }, 1500);
  };

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case "all": return "from-red-500 to-red-600";
      case "beginner": return "from-green-500 to-green-600";
      case "intermediate": return "from-orange-500 to-orange-600";
      case "advanced": return "from-red-500 to-red-600";
      default: return "from-gray-500 to-gray-600";
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 shadow-2xl border-b-4 border-red-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
            <div className="text-center">
              <div className="flex flex-col sm:flex-row items-center justify-center mb-6 sm:mb-8">
                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
                  <div className="h-20 w-20 sm:h-32 sm:w-32 flex items-center justify-center relative z-10">
                    <span className="text-5xl sm:text-8xl font-black text-red-700">습</span>
                  </div>
                </div>
                <div className="sm:ml-6 mt-4 sm:mt-0">
                  <h1 className="text-3xl sm:text-5xl font-black text-gray-900 mb-2">Korean Practice Hub</h1>
                  <p className="text-lg sm:text-2xl text-red-700 font-black">한국어 연습 허브</p>
                  <p className="text-sm sm:text-lg text-gray-600 font-semibold">(Korean Practice Hub)</p>
                </div>
              </div>
              
              <p className="text-xl text-gray-800 max-w-4xl mx-auto leading-relaxed font-semibold mb-8">
                Test your Korean knowledge with interactive quizzes, sentence practice, and vocabulary challenges
              </p>

              {/* Practice Mode Selection */}
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4 mb-8">
                {practiceModes.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setPracticeMode(mode.id)}
                    className={`px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center ${
                      practiceMode === mode.id
                        ? "bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-xl"
                        : "bg-white text-gray-700 hover:bg-red-50 border-2 border-red-200 hover:border-red-400"
                    }`}
                  >
                    <span className="text-xl sm:text-2xl mr-2 sm:mr-3">{mode.icon}</span>
                    {mode.name}
                  </button>
                ))}
              </div>

              {/* Difficulty Selection */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {difficulties.map((diff) => (
                  <button
                    key={diff.id}
                    onClick={() => setDifficulty(diff.id)}
                    className={`px-6 py-3 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg ${
                      difficulty === diff.id
                        ? `bg-gradient-to-r ${getDifficultyColor(diff.id)} text-white shadow-xl`
                        : "bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    {diff.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="bg-gradient-to-br from-red-100 to-pink-100 rounded-3xl shadow-xl p-8 border-4 border-red-200 text-center">
              <div className="text-6xl font-black text-red-700 mb-2">{accuracy}%</div>
              <div className="text-xl font-bold text-gray-800 mb-2">Accuracy</div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="h-3 rounded-full bg-gradient-to-r from-red-400 to-red-600"
                  style={{ width: `${accuracy}%` }}
                ></div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-3xl shadow-xl p-8 border-4 border-green-200 text-center">
              <div className="text-6xl font-black text-green-700 mb-2">{score}</div>
              <div className="text-xl font-bold text-gray-800 mb-2">Correct Answers</div>
              <div className="text-sm text-gray-600 font-semibold">Keep it up! 🚀</div>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-3xl shadow-xl p-8 border-4 border-orange-200 text-center">
              <div className="text-6xl font-black text-orange-700 mb-2">{streak}</div>
              <div className="text-xl font-bold text-gray-800 mb-2">Current Streak</div>
              <div className="text-sm text-gray-600 font-semibold">Start your streak!</div>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-3xl shadow-xl p-8 border-4 border-purple-200 text-center">
              <div className="text-6xl font-black text-purple-700 mb-2">{bestStreak}</div>
              <div className="text-xl font-bold text-gray-800 mb-2">Best Streak</div>
              <div className="text-sm text-gray-600 font-semibold">Personal best! 🏆</div>
            </div>
          </div>

          {/* Main Practice Area */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-xl p-12 border-4 border-red-200">
            {practiceMode === "vocabulary" && (
              <div>
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Vocabulary Quiz</h2>
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <span className="text-lg font-semibold text-gray-700">Choose Category:</span>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="px-4 py-2 rounded-xl border-2 border-red-200 focus:border-red-400 focus:outline-none font-semibold"
                    >
                      {Object.keys(vocabData).map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {!quizCompleted ? (
                  currentQuestion && (
                    <div className="bg-white rounded-3xl shadow-2xl p-12 border-4 border-red-200">
                      <div className="text-center mb-8">
                        <h3 className="text-4xl font-black text-gray-900 mb-4">
                          Question {questionIndex + 1} of {totalQuestions}
                        </h3>
                        <div className="w-full bg-gray-200 rounded-full h-4 mb-8">
                          <div 
                            className="h-4 rounded-full bg-gradient-to-r from-red-500 to-orange-500 shadow-lg transition-all duration-500"
                            style={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}
                          ></div>
                        </div>
                        <h4 className="text-3xl font-bold text-red-700 mb-4">
                          {currentQuestion.question}
                        </h4>
                        <div className="mb-8">
                          <AudioPlayer word={currentQuestion.question} roman={currentQuestion.question} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {currentQuestion.answers.map((answer, index) => (
                          <button
                            key={index}
                            onClick={() => handleAnswer(answer)}
                            disabled={showResult}
                            className={`px-8 py-6 rounded-2xl font-bold text-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                              showResult
                                ? answer === correctAnswer
                                  ? "bg-green-500 text-white"
                                  : answer === selectedAnswer
                                  ? "bg-red-500 text-white"
                                  : "bg-gray-200 text-gray-500"
                                : "bg-gradient-to-r from-red-100 to-orange-100 text-gray-800 hover:from-red-200 hover:to-orange-200 border-2 border-red-300 hover:border-red-500"
                            }`}
                          >
                            {answer}
                          </button>
                        ))}
                      </div>

                      {showResult && (
                        <div className="text-center mt-8">
                          <div className={`text-4xl font-black mb-4 ${
                            selectedAnswer === correctAnswer ? "text-green-600" : "text-red-600"
                          }`}>
                            {selectedAnswer === correctAnswer ? "Correct! ✅" : "Incorrect ❌"}
                          </div>
                          {selectedAnswer !== correctAnswer && (
                            <p className="text-xl text-gray-700 font-semibold">
                              The correct answer is: <span className="text-green-600 font-bold">{correctAnswer}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <div className="text-center">
                    <div className="text-8xl mb-8">🎉</div>
                    <h2 className="text-4xl font-black text-gray-900 mb-6">Quiz Complete!</h2>
                    <p className="text-2xl text-gray-700 mb-8 font-semibold">
                      You scored {score} out of {totalQuestions} correct!
                    </p>
                    <div className="text-6xl font-black text-red-600 mb-8">
                      {accuracy}%
                    </div>
                    <button
                      onClick={resetQuiz}
                      className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-12 py-6 rounded-2xl font-bold text-xl hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            )}

            {practiceMode === "sentence" && (
              <div>
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Sentence Practice</h2>
                  <p className="text-lg text-gray-700 font-semibold mb-6">
                    Practice constructing Korean sentences and grammar
                  </p>
                </div>

                {!quizCompleted ? (
                  currentQuestion && (
                    <div className="bg-white rounded-3xl shadow-2xl p-12 border-4 border-red-200">
                      <div className="text-center mb-8">
                        <h3 className="text-4xl font-black text-gray-900 mb-4">
                          Question {questionIndex + 1} of {totalQuestions}
                        </h3>
                        <div className="w-full bg-gray-200 rounded-full h-4 mb-8">
                          <div 
                            className="h-4 rounded-full bg-gradient-to-r from-red-500 to-orange-500 shadow-lg transition-all duration-500"
                            style={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}
                          ></div>
                        </div>
                        <h4 className="text-3xl font-bold text-red-700 mb-4">
                          {currentQuestion.question}
                        </h4>
                        <div className="mb-8">
                          <AudioPlayer word={currentQuestion.question} roman={currentQuestion.question} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {currentQuestion.answers.map((answer, index) => (
                          <button
                            key={index}
                            onClick={() => handleAnswer(answer)}
                            disabled={showResult}
                            className={`px-8 py-6 rounded-2xl font-bold text-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                              showResult
                                ? answer === correctAnswer
                                  ? "bg-green-500 text-white"
                                  : answer === selectedAnswer
                                  ? "bg-red-500 text-white"
                                  : "bg-gray-200 text-gray-500"
                                : "bg-gradient-to-r from-red-100 to-orange-100 text-gray-800 hover:from-red-200 hover:to-orange-200 border-2 border-red-300 hover:border-red-500"
                            }`}
                          >
                            {answer}
                          </button>
                        ))}
                      </div>

                      {showResult && (
                        <div className="text-center mt-8">
                          <div className={`text-4xl font-black mb-4 ${
                            selectedAnswer === correctAnswer ? "text-green-600" : "text-red-600"
                          }`}>
                            {selectedAnswer === correctAnswer ? "Correct! ✅" : "Incorrect ❌"}
                          </div>
                          {selectedAnswer !== correctAnswer && (
                            <p className="text-xl text-gray-700 font-semibold">
                              The correct answer is: <span className="text-green-600 font-bold">{correctAnswer}</span>
                            </p>
                          )}
                          {currentQuestion.explanation && (
                            <p className="text-lg text-gray-600 mt-4 font-medium">
                              {currentQuestion.explanation}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <div className="text-center">
                    <div className="text-8xl mb-8">🎉</div>
                    <h2 className="text-4xl font-black text-gray-900 mb-6">Sentence Practice Complete!</h2>
                    <p className="text-2xl text-gray-700 mb-8 font-semibold">
                      You scored {score} out of {totalQuestions} correct!
                    </p>
                    <div className="text-6xl font-black text-red-600 mb-8">
                      {accuracy}%
                    </div>
                    <button
                      onClick={resetQuiz}
                      className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-12 py-6 rounded-2xl font-bold text-xl hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            )}

            {practiceMode === "mixed" && (
              <div>
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-black text-gray-900 mb-4">Mixed Challenge</h2>
                  <p className="text-lg text-gray-700 font-semibold mb-6">
                    Test your knowledge with vocabulary, grammar, and culture questions
                  </p>
                </div>

                {!quizCompleted ? (
                  currentQuestion && (
                    <div className="bg-white rounded-3xl shadow-2xl p-12 border-4 border-red-200">
                      <div className="text-center mb-8">
                        <h3 className="text-4xl font-black text-gray-900 mb-4">
                          Question {questionIndex + 1} of {totalQuestions}
                        </h3>
                        <div className="w-full bg-gray-200 rounded-full h-4 mb-8">
                          <div 
                            className="h-4 rounded-full bg-gradient-to-r from-red-500 to-orange-500 shadow-lg transition-all duration-500"
                            style={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}
                          ></div>
                        </div>
                        <h4 className="text-3xl font-bold text-red-700 mb-4">
                          {currentQuestion.question}
                        </h4>
                        <div className="mb-8">
                          <AudioPlayer word={currentQuestion.question} roman={currentQuestion.question} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {currentQuestion.answers.map((answer, index) => (
                          <button
                            key={index}
                            onClick={() => handleAnswer(answer)}
                            disabled={showResult}
                            className={`px-8 py-6 rounded-2xl font-bold text-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                              showResult
                                ? answer === correctAnswer
                                  ? "bg-green-500 text-white"
                                  : answer === selectedAnswer
                                  ? "bg-red-500 text-white"
                                  : "bg-gray-200 text-gray-500"
                                : "bg-gradient-to-r from-red-100 to-orange-100 text-gray-800 hover:from-red-200 hover:to-orange-200 border-2 border-red-300 hover:border-red-500"
                            }`}
                          >
                            {answer}
                          </button>
                        ))}
                      </div>

                      {showResult && (
                        <div className="text-center mt-8">
                          <div className={`text-4xl font-black mb-4 ${
                            selectedAnswer === correctAnswer ? "text-green-600" : "text-red-600"
                          }`}>
                            {selectedAnswer === correctAnswer ? "Correct! ✅" : "Incorrect ❌"}
                          </div>
                          {selectedAnswer !== correctAnswer && (
                            <p className="text-xl text-gray-700 font-semibold">
                              The correct answer is: <span className="text-green-600 font-bold">{correctAnswer}</span>
                            </p>
                          )}
                          {currentQuestion.explanation && (
                            <p className="text-lg text-gray-600 mt-4 font-medium">
                              {currentQuestion.explanation}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <div className="text-center">
                    <div className="text-8xl mb-8">🎉</div>
                    <h2 className="text-4xl font-black text-gray-900 mb-6">Mixed Challenge Complete!</h2>
                    <p className="text-2xl text-gray-700 mb-8 font-semibold">
                      You scored {score} out of {totalQuestions} correct!
                    </p>
                    <div className="text-6xl font-black text-red-600 mb-8">
                      {accuracy}%
                    </div>
                    <button
                      onClick={resetQuiz}
                      className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-12 py-6 rounded-2xl font-bold text-xl hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Practice;